'use client'

import { useState } from 'react'
import { cn } from '../../utils/cn'
import { Check } from 'lucide-react'
import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { Badge } from '../common/Badge'
import { Button } from '../common/Button'
import { FadeIn } from '../motion/FadeIn'
import { StaggerChildren } from '../motion/StaggerChildren'

interface Plan {
  name: string
  price: string | number
  period?: string
  description: string
  features: string[]
  cta: {
    text: string
    href: string
  }
  highlighted?: boolean
  badge?: string
  annualPrice?: string
  annualPeriod?: string
}

interface PricingProps {
  id?: string
  sectionNumber?: string
  heading: string
  subheading?: string
  plans: Plan[]
  billingToggle?: boolean
  footnote?: string
  columns?: 2 | 3 | 4
}

export function Pricing({
  id,
  sectionNumber,
  heading,
  subheading,
  plans,
  billingToggle = false,
  footnote,
  columns
}: PricingProps) {
  // Auto-detect columns: 4 plans = 4 cols, otherwise 3
  const gridCols = columns ?? (plans.length === 4 ? 4 : 3)
  const [isAnnual, setIsAnnual] = useState(false)

  return (
    <Section id={id} number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}

          {/* Billing Toggle */}
          {billingToggle && (
            <FadeIn delay={0.2}>
              <div className="flex items-center justify-center gap-4 mt-8">
                <span
                  className={cn(
                    'text-sm',
                    !isAnnual
                      ? 'text-[var(--landing-text-primary)]'
                      : 'text-[var(--landing-text-muted)]'
                  )}
                >
                  Monthly
                </span>
                <button
                  type="button"
                  onClick={() => setIsAnnual(!isAnnual)}
                  aria-label={isAnnual ? 'Switch to monthly billing' : 'Switch to annual billing'}
                  className={cn(
                    'relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0',
                    isAnnual ? 'bg-emerald-500' : 'bg-zinc-600'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform duration-200 shadow-sm',
                      isAnnual ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
                <span
                  className={cn(
                    'text-sm',
                    isAnnual
                      ? 'text-[var(--landing-text-primary)]'
                      : 'text-[var(--landing-text-muted)]'
                  )}
                >
                  Annual
                  <Badge variant="accent" size="sm" className="ml-2">
                    Save 20%
                  </Badge>
                </span>
              </div>
            </FadeIn>
          )}
        </div>

        {/* Pricing Cards */}
        <StaggerChildren
          className={cn(
            'grid gap-6 mx-auto',
            gridCols === 2 && 'md:grid-cols-2 max-w-3xl',
            gridCols === 3 && 'md:grid-cols-2 lg:grid-cols-3 max-w-5xl',
            gridCols === 4 && 'md:grid-cols-2 lg:grid-cols-4 max-w-6xl'
          )}
          staggerDelay={0.1}
        >
          {plans.map((plan, index) => (
            <div
              key={index}
              className={cn(
                'relative flex flex-col rounded-2xl p-6',
                plan.highlighted
                  ? 'landing-card-highlighted'
                  : 'landing-card'
              )}
            >
              {/* Badge */}
              {plan.badge && (
                <Badge
                  variant="accent"
                  size="sm"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 shadow-lg"
                >
                  {plan.badge}
                </Badge>
              )}

              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-[var(--landing-text-muted)] mb-4">
                  {plan.description}
                </p>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-[var(--landing-text-primary)]">
                      {isAnnual && typeof plan.price === 'number'
                        ? `$${Math.round(plan.price * 0.8)}`
                        : typeof plan.price === 'number'
                          ? `$${plan.price}`
                          : plan.price}
                    </span>
                    <span className="text-sm text-[var(--landing-text-muted)]">
                      {typeof plan.price === 'number' ? '/mo' : ''}
                    </span>
                  </div>
                  {isAnnual && typeof plan.price === 'number' && (
                    <p className="text-xs text-[var(--landing-text-muted)] mt-1">
                      <span className="line-through">${plan.price}/mo</span>
                      <span className="ml-1.5 text-emerald-400">billed ${Math.round(plan.price * 0.8 * 12)}/yr</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-3 mb-6">
                {plan.features.map((feature, featureIndex) => (
                  <li
                    key={featureIndex}
                    className="flex items-start gap-3 text-sm text-[var(--landing-text-secondary)]"
                  >
                    <Check className="h-4 w-4 text-[var(--accent)] flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                href={plan.cta.href}
                variant={plan.highlighted ? 'primary' : 'outline'}
                size="lg"
                glow={plan.highlighted}
                className="w-full justify-center"
              >
                {plan.cta.text}
              </Button>
            </div>
          ))}
        </StaggerChildren>

        {/* Footnote */}
        {footnote && (
          <FadeIn delay={0.3}>
            <p className="text-center text-sm text-[var(--landing-text-muted)] mt-8">
              {footnote}
            </p>
          </FadeIn>
        )}
      </Container>
    </Section>
  )
}
